import classNames from "classnames";
import { FieldMetaProps } from "formik";
import { debounce } from "lodash";
import React, { useCallback, useEffect, useMemo, useState } from "react";
import { focusNextElement } from "src/utils/index";
import { OCFormControl } from "./FormControl";
import { alpha, Grid } from "@mui/material";
import { FieldError } from "./FieldError";
import { FormLabel } from "@mui/material";
import { OCTextField } from "./TextField";
import { PatternFormat } from "react-number-format";

export interface ITimeFieldProps {
  name: string;
  id?: string;
  className?: any;
  classes?: any;
  value?: string;
  disabled?: boolean;
  onChange?: (time: string, error?: string) => void;
  onFocus?: (event: React.FocusEvent<HTMLInputElement>) => void;
  onBlur?: (event: React.FocusEvent<HTMLInputElement>) => void;
  timeRegex?: (string | RegExp)[];
  guide?: boolean;
  placeholder?: string;
  placeholderChar?: string;
  keepCharPositions?: boolean;
  showMask?: boolean;
  meta?: Partial<FieldMetaProps<any>>;
  label?: string;
  labelSX?: any;
}

interface IFormaTimeObject {
  time: string;
  error: string;
}

const TimeFieldStyles = (theme: any): any => ({
  timeInput: {
    "& .error": {
      boxShadow: `${alpha(theme.palette.error.dark, 0.25)} 0 0 0 0.2rem !important`,
      transition: theme.transitions.create(["border-color", "background-color", "box-shadow"]),
      borderColor: `${theme.palette.error.dark} !important`,
      backgroundColor: `${alpha(theme.palette.error.dark, 0.25)} !important`,
    },
    "& input": {
      borderRadius: "4px",
      position: "relative",
      width: "50px",
      backgroundColor: theme.custom.forms.inputBack,
      border: "1px solid",
      borderColor: theme.custom.forms.inputBorder,
      fontSize: 11,
      padding: "3px 6px",
      color: theme.palette.common.white,
      transition: theme.transitions.create(["border-color", "background-color", "box-shadow"]),
      "&:focus, &:hover": {
        boxShadow: `${alpha(theme.palette.primary.main, 0.25)} 0 0 0 0.2rem`,
        borderColor: theme.palette.primary.main,
      },
      "&::placeholder, &::-webkit-input-placeholder": {
        color: theme.custom.forms.labels,
        opacity: 1,
      },
    },
  },
});

// TIMEFIELD COMPONENT
export const TimeField = (props: ITimeFieldProps): JSX.Element => {
  const {
    name,
    id,
    className,
    label,
    placeholder,
    onChange,
    onBlur,
    onFocus,
    value,
    disabled,
    timeRegex,
    meta,
    labelSX,
  } = props;

  const [isFocused, setIsFocused] = useState<boolean>(false);
  const disabledStyle: any = disabled ? { opacity: 0.5, userSelect: "none", pointerEvents: "none" } : null;

  // Track onChange event internally as user types (this value is unformatted)
  const [internalValue, setInternalValue] = useState<string>("00:00");
  useEffect(() => {
    setInternalValue(value ?? "00:00");
  }, [value]);

  const handleUpdateValue = (value: string, pad: boolean): { time: string; error: string | null } => {
    let unmaskedValue = pad ? value.replace(/[^0-9]/g, "") : value;
    const timeRegex = /^(0[0-9]|1[0-9]|2[0-3]):([0-5][0-9])$/;
    if (pad && unmaskedValue.length < 4) {
      if (unmaskedValue.length <= 2) {
        unmaskedValue = unmaskedValue.padEnd(4, "0");
      }
      unmaskedValue = unmaskedValue.padStart(4, "0");
    }
    const truncatedValue = unmaskedValue.slice(0, 4);
    const paddedValue = truncatedValue.replace(/^(\d{0,2})(\d{0,2})$/, (_, hh, mm) => {
      if (parseInt(hh) > 23) {
        hh = "23";
      }
      if (parseInt(mm) > 59) {
        mm = "59";
      }
      const paddedHours = hh ? (pad ? hh.padStart(2, "0") : hh) : "00";
      return `${paddedHours}:${mm.padStart(2, "0")}`;
    });
    setInternalValue(paddedValue);

    const error = timeRegex.test(paddedValue) ? null : "Invalid time/format";
    return { time: paddedValue, error: error };
  };

  // Track if formik value is dirty.
  const dirty: boolean = useMemo(() => {
    if (!meta) return false;
    const { initialValue, touched } = meta;
    return touched && value !== initialValue ? true : false;
  }, [meta, value]);

  const hasError = meta?.error ? true : false;

  return (
    <OCFormControl error={hasError} sx={{ width: "auto", my: 0.3 }}>
      <Grid container sx={{ display: "flex", alignItems: "start" }}>
        <Grid
          item
          sx={{
            display: "flex",
            justifyContent: hasError ? "space-between" : "flex-end",
            textAlign: "right",
          }}
          xs
        >
          {label && (
            <>
              <FieldError meta={meta} icon={true} />
              <FormLabel sx={{ ...labelSX }} htmlFor={"id-" + name + "-date"}>
                {label}
              </FormLabel>
            </>
          )}
        </Grid>
        <Grid item xs={7} sx={(theme) => TimeFieldStyles(theme).timeInput}>
          <PatternFormat
            allowEmptyFormatting={true}
            format="##:##"
            mask="_"
            value={internalValue}
            onChange={(event) => {
              const { time, error } = handleUpdateValue(event.target.value, false);
              if (typeof onChange === "function") onChange(time, error);
            }}
            onBlur={(event) => {
              const { time, error } = handleUpdateValue(event.target.value, true);
              if (typeof onChange === "function") onChange(time, error);
              if (typeof onBlur === "function") onBlur(event);
            }}
            className={`${className} time-input`}
            disabled={disabled}
            name={name}
            autoComplete="off"
            autoCorrect="off"
            autoCapitalize="off"
            pattern="[0-9:]+"
            placeholder={placeholder}
          />
        </Grid>
      </Grid>
    </OCFormControl>
  );
};
